import { useState } from "react";
import { Pencil } from "lucide-react";
import {
  Button,
  IconButton,
  Modal,
  SectionCard,
  StatusBadge,
} from "../../components/ui";
import { pontoService } from "../../services/apiService";
import { useNotification } from "../../hooks/useNotification";
import { Tabela, Td, Th } from "./TabelaCompacta";
import { DIAS_SEMANA, dataCompleta, minutosParaHora } from "./pontoUtils";
import HorarioTrabalhoCampos, {
  formularioParaJornada,
  jornadaParaFormulario,
} from "./HorarioTrabalhoCampos";

const descreverTurnos = (e1, s1, e2, s2) =>
  e1 && s1 ? `${e1}–${s1}${e2 && s2 ? ` / ${e2}–${s2}` : ""}` : null;

const resumoJornada = (jornada) => {
  const semana = descreverTurnos(
    jornada.semanaEntrada1,
    jornada.semanaSaida1,
    jornada.semanaEntrada2,
    jornada.semanaSaida2,
  );
  const sabado = descreverTurnos(
    jornada.sabadoEntrada1,
    jornada.sabadoSaida1,
    jornada.sabadoEntrada2,
    jornada.sabadoSaida2,
  );
  if (semana || sabado) {
    return [semana && `Seg a Sex ${semana}`, sabado && `Sáb ${sabado}`]
      .filter(Boolean)
      .join(" · ");
  }
  // Jornada antiga, cadastrada só pela carga de cada dia.
  return DIAS_SEMANA.map(({ campo, rotulo }) =>
    jornada[campo]
      ? `${rotulo.slice(0, 3)} ${minutosParaHora(jornada[campo])}`
      : null,
  )
    .filter(Boolean)
    .join(" · ");
};

/**
 * Quem controla ponto, cargo e horário de trabalho. É o mesmo cadastro da seção
 * "Ponto e horário de trabalho" em Cadastros > Usuários.
 */
const JornadasTab = ({ colaboradoras, onAtualizado }) => {
  const { showError, showSuccess } = useNotification();
  const [editando, setEditando] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const abrir = (colaboradora) => {
    const formulario = jornadaParaFormulario(colaboradora.jornada);
    setEditando({
      colaboradora,
      formulario: colaboradora.jornada
        ? formulario
        : { ...formulario, participa: true },
    });
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      await pontoService.salvarJornada(
        editando.colaboradora.usuarioId,
        formularioParaJornada(editando.formulario),
      );
      showSuccess("Horário de trabalho salvo.");
      setEditando(null);
      await onAtualizado();
    } catch (erro) {
      showError(erro.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <>
      <SectionCard bodyClassName="p-0">
        <p className="border-b border-gray-200 px-4 py-3 text-sm text-gray-600">
          Usuários desta organização. Quem controla ponto pode bater o ponto e
          aparece no cartão. Também dá para editar em Cadastros &gt; Usuários.
        </p>
        <Tabela>
          <thead>
            <tr>
              <Th>Nome</Th>
              <Th>Cargo</Th>
              <Th>Situação no ponto</Th>
              <Th>Início</Th>
              <Th>Horário de trabalho</Th>
              <Th className="text-right">Editar</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {colaboradoras.map((colaboradora) => (
              <tr key={colaboradora.usuarioId}>
                <Td>
                  <p className="font-medium text-gray-900">
                    {colaboradora.nome}
                  </p>
                  <p className="text-xs text-gray-500">{colaboradora.email}</p>
                </Td>
                <Td>{colaboradora.jornada?.cargo || "-"}</Td>
                <Td>
                  {!colaboradora.jornada ? (
                    <StatusBadge>Fora do ponto</StatusBadge>
                  ) : colaboradora.jornada.ativo ? (
                    <StatusBadge variant="success">Ativa</StatusBadge>
                  ) : (
                    <StatusBadge variant="warning">Desativada</StatusBadge>
                  )}
                </Td>
                <Td className="tabular-nums">
                  {colaboradora.jornada
                    ? dataCompleta(colaboradora.jornada.dataInicio)
                    : "-"}
                </Td>
                <Td className="text-xs">
                  {colaboradora.jornada
                    ? resumoJornada(colaboradora.jornada)
                    : "-"}
                </Td>
                <Td className="text-right">
                  <IconButton
                    size="sm"
                    icon={Pencil}
                    label={`Editar horário de ${colaboradora.nome}`}
                    onClick={() => abrir(colaboradora)}
                  />
                </Td>
              </tr>
            ))}
          </tbody>
        </Tabela>
      </SectionCard>

      <Modal
        open={Boolean(editando)}
        onClose={() => setEditando(null)}
        title="Ponto e horário de trabalho"
        description={editando?.colaboradora.nome}
        className="max-w-2xl"
      >
        {editando && (
          <div className="space-y-4">
            <HorarioTrabalhoCampos
              formulario={editando.formulario}
              onChange={(formulario) =>
                setEditando({ ...editando, formulario })
              }
              prefixoId="modal-jornada"
            />
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditando(null)}>
                Cancelar
              </Button>
              <Button
                loading={salvando}
                disabled={!editando.formulario.dataInicio}
                onClick={salvar}
              >
                Salvar
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default JornadasTab;
